import React, { useState } from "react";
import { observer } from "mobx-react";
import { useFormik } from "formik";
import { Button, Input, Field } from "@chakra-ui/react";
import { t } from "i18next";
import {
  OsaWayFormData,
  CreateOrUpdateOsaWaySchema,
} from "./CreateOrUpdateOsaWaySchema";

import { useStores } from "../../../shared/stores/RootStore";
import { toast } from "../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import { CreateOsaWay, OsaWay, UpdateOsaWay } from "../../models/OsaWay";

const initialValues: OsaWayFormData = {
  name: "",
};

const CreateOrUpdateOsaWayModalBase: React.FC<{
  onClose: () => void;
  osaWay?: OsaWay;
}> = ({ osaWay, onClose }) => {
  const { osaWayStore, campaignStore } = useStores();
  const [submitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: osaWay || initialValues,

    onSubmit: async (values: OsaWayFormData) => {
      setSubmitting(true);
      if (osaWay) {
        await updateRoute(values);
      } else {
        await createRoute(values);
      }
      setSubmitting(false);

      formik.setValues(formik.initialValues);
    },
    validationSchema: CreateOrUpdateOsaWaySchema,
  });

  const createRoute = async (values: OsaWayFormData) => {
    const createRequest: CreateOsaWay = {
      name: values.name,
      campaignId: campaignStore.selected!.id,
    };
    await osaWayStore.createOsaWay(createRequest).then(() => {
      toast("success", t("home:project.routeCreated"));
      onClose();
    });
  };

  const updateRoute = async (values: OsaWayFormData) => {
    const updateRequest: UpdateOsaWay = {
      name: values.name,
      description: osaWay!.description || "",
      plannedTrajectory: osaWay!.plannedTrajectory,
      plannedDistance: osaWay!.plannedDistance, // NEED TO ADD DISTANCE
    };
    await osaWayStore
      .updateOsaWay(osaWay!.id, updateRequest)
      .then(() => {
        toast("success", "Route saved");
        onClose();
      })
      .catch((e) => {
        toast("success", "Unable to save route");
        console.warn(e);
      });
  };

  return (
    <CustomDialog
      header={t(
        osaWay ? "shared:route.update.title" : "shared:route.create.title"
      )}
      footer={
        <Button
          loading={submitting}
          colorPalette="primary"
          type="submit"
          form="createOrUpdateOsaWay"
          disabled={!formik.isValid || !formik.dirty}
        >
          {`${t("shared:project.submit.done")}`}
        </Button>
      }
      onClose={onClose}
    >
      <form id="createOrUpdateOsaWay" onSubmit={formik.handleSubmit}>
        <Field.Root invalid={!!formik.errors.name}>
          <Field.Label>{t("shared:route.fields.name.title")}</Field.Label>
          <Input
            required
            placeholder={`${t("shared:route.fields.name.placeholder")}`}
            name="name"
            onChange={formik.handleChange}
            value={formik.values.name}
          />
          <Field.ErrorText>{formik.errors.name}</Field.ErrorText>
        </Field.Root>
      </form>
    </CustomDialog>
  );
};

export const CreateOrUpdateOsaWayModal = observer(
  CreateOrUpdateOsaWayModalBase
);
